import React, { ReactElement, useMemo } from 'react'
import { SplashPageSuccessRateSection } from '@/components/static/SplashPage/SplashPageMiddleSection/SplashPageSuccessRateSection/SplashPageSuccessRateSection'
import styles from './SplashPageMiddleSection.module.scss'
import { ValueProposition } from '@/components/static/SplashPage/SplashPageMiddleSection/SplashPageSuccessRateSection/ValueProposition/ValueProposition'
import { PhilosophyOwl } from '@/components/static/images/owlBranding/philosophy'
import { Friends } from '@/components/static/images/owlBranding/friends'
import { Screenshot } from '@/components/static/images/Screenshot/Screenshot'
import { ColorMap, getJepColor } from '@/components/static/ui/consts'
import { CourseConfigType } from 'common/src/courseConfig'
import { jepAssertNever } from 'common/src/utils/jepAssertNever'
import { ErrorBoundary } from '@/components/static/ErrorBoundary/ErrorBoundary'
import { UiLayoutConstant } from '@/globals/uiLayoutConstant'
import { ScreenshotComposition } from '@/components/static/SplashPage/SplashPageMiddleSection/ScreenshotComposition/ScreenshotComposition'

interface SplashPageMiddleSectionProps {
    isMobileMode: boolean
    courseConfigType: CourseConfigType
}

// The FSA Health app has the agentic Eupheus (conversations, cited sources,
// page awareness).
const getAiAssistantTitleForCourseConfigType = (
    courseConfigType: CourseConfigType
): string => {
    switch (courseConfigType) {
        case CourseConfigType.fsaHealth:
            return 'Meet Eupheus, Your AI Study Partner'
        default:
            jepAssertNever(
                courseConfigType,
                'Unhandled courseConfigType for getAiAssistantTitleForCourseConfigType'
            )
    }
}

const getAiAssistantDescriptionForCourseConfigType = (
    courseConfigType: CourseConfigType
): string => {
    switch (courseConfigType) {
        case CourseConfigType.fsaHealth:
            return "Ask anything about your exam. Eupheus doesn't answer from memory — it researches your actual outlines, notecards, questions and source material, then links every source it used, so you can check its work. Still want a human? Our course instructors are one button away."
        default:
            jepAssertNever(
                courseConfigType,
                'Unhandled courseConfigType for getAiAssistantDescriptionForCourseConfigType'
            )
    }
}

const getAiAssistantMediaForCourseConfigType = (
    courseConfigType: CourseConfigType
): ReactElement => {
    switch (courseConfigType) {
        case CourseConfigType.fsaHealth:
            return (
                <ScreenshotComposition
                    lead={{
                        src: '/splashPage/middleSection/eupheusMnemonic.webp',
                        previewSrc:
                            '/splashPage/middleSection/eupheusMnemonicFull.webp',
                        alt: 'Eupheus surfacing the mnemonic already on the course notecard for the seven provider reimbursement models, with a table explaining each and links to the chapter',
                    }}
                    supporting={[
                        {
                            src: '/splashPage/middleSection/eupheusStudyPlan.webp',
                            previewSrc:
                                '/splashPage/middleSection/eupheusStudyPlanFull.webp',
                            alt: 'Eupheus building a two-week study plan for risk adjustment, linking specific notecards and past exam questions',
                        },
                        {
                            src: '/splashPage/middleSection/eupheusStopLoss.webp',
                            previewSrc:
                                '/splashPage/middleSection/eupheusStopLossFull.webp',
                            alt: 'Eupheus comparing specific and aggregate stop-loss in a table, with cited sources',
                        },
                    ]}
                />
            )
        default:
            jepAssertNever(
                courseConfigType,
                'Unhandled courseConfigType for getAiAssistantMediaForCourseConfigType'
            )
    }
}

// The Eupheus avatar beside the heading — the same mark students click in
// the product, so the splash and the app teach the same icon.
const getAiAssistantTitleImageForCourseConfigType = (
    courseConfigType: CourseConfigType
): string | undefined => {
    switch (courseConfigType) {
        case CourseConfigType.fsaHealth:
            return '/owlBranding/eupheusChatFab.webp'
        default:
            jepAssertNever(
                courseConfigType,
                'Unhandled courseConfigType for getAiAssistantTitleImageForCourseConfigType'
            )
    }
}

// Only the FSA Health app has a Eupheus page to send readers to.
const getAiAssistantLinkForCourseConfigType = (
    courseConfigType: CourseConfigType
): { href: string; text: string } | undefined => {
    switch (courseConfigType) {
        case CourseConfigType.fsaHealth:
            return {
                href: UiLayoutConstant.EUPHEUS_PAGE,
                text: 'See what Eupheus can do →',
            }
        default:
            jepAssertNever(
                courseConfigType,
                'Unhandled courseConfigType for getAiAssistantLinkForCourseConfigType'
            )
    }
}

const getOneStopShopDescriptionForCourseConfigType = (
    courseConfigType: CourseConfigType
): string => {
    switch (courseConfigType) {
        case CourseConfigType.fsaHealth:
            return 'Access our extensive inventory of outlines, notecards and practice problems from your laptop or phone - all you need is internet!'
        default:
            jepAssertNever(
                courseConfigType,
                'Unknown courseConfigType for getOneStopShopDescriptionForCourseConfigType'
            )
    }
}

const getSecondValuePropositionSectionForCourseConfigType = (
    courseConfigType: CourseConfigType,
    isMobileMode: boolean
): ReactElement => {
    const colors = getJepColor(courseConfigType)
    switch (courseConfigType) {
        case CourseConfigType.fsaHealth:
            return (
                <>
                    <ValueProposition
                        title={'Autograde Any Question — Excel Included'}
                        link={{
                            href: UiLayoutConstant.EUPHEUS_PAGE,
                            text: 'See how autograding works →',
                        }}
                        description={
                            'Grade a single question or a whole quiz in one click. Eupheus scores every part, explains each point you did and did not earn against the official solution, and hands your uploaded Excel workbook back marked up — problem cells flagged in red with notes on how to fix them.'
                        }
                        image={
                            <ScreenshotComposition
                                lead={{
                                    src: '/splashPage/middleSection/autogradeMarkedUpWorkbook.webp',
                                    previewSrc:
                                        '/splashPage/middleSection/autogradeMarkedUpWorkbookFull.webp',
                                    alt: 'the grader-annotated copy of a student Excel workbook, with the wrong cells highlighted in red, notes explaining each mistake, and a Grader Feedback sheet',
                                }}
                                supporting={[
                                    {
                                        src: '/splashPage/middleSection/autogradeFeedback.webp',
                                        previewSrc:
                                            '/splashPage/middleSection/autogradeFeedbackFull.webp',
                                        alt: 'autograde feedback listing how to earn more points next time, with buttons to download your workbook and the marked-up copy',
                                    },
                                ]}
                            />
                        }
                        titleFontColors={{
                            desktop: colors.colorThree,
                            mobile: colors.colorFive,
                        }}
                        isMobileMode={isMobileMode}
                        backgroundColors={{
                            desktop: colors.highlight,
                            mobile: colors.lightGray,
                        }}
                    />
                    <div className={styles.multipleValuePropositions}>
                        <ValueProposition
                            title={'Customizable Notecard Sets and Quizzes'}
                            description={
                                'Test yourself on exactly what you want. Or sit back and let us build your study tools for you.'
                            }
                            image={
                                <Screenshot
                                    src={
                                        '/splashPage/middleSection/buildANotecardSet.webp'
                                    }
                                    alt={
                                        'screenshot of building a notecard set'
                                    }
                                />
                            }
                            titleFontColors={{
                                desktop: colors.colorFive,
                                mobile: colors.colorThree,
                            }}
                            isMobileMode={isMobileMode}
                            backgroundColors={{
                                desktop: colors.highlight,
                                mobile: colors.highlight,
                            }}
                        />
                        <ValueProposition
                            title={'Social Features'}
                            description={
                                'Our cutting-edge platform fosters dynamic interactions with instructors and fellow students, promoting enriched learning.'
                            }
                            image={<Friends />}
                            titleFontColors={{
                                desktop: colors.colorFive,
                                mobile: colors.colorFive,
                            }}
                            isMobileMode={isMobileMode}
                            backgroundColors={{
                                desktop: colors.highlight,
                                mobile: colors.lightGray,
                            }}
                        />
                    </div>
                </>
            )
        default:
            jepAssertNever(
                courseConfigType,
                'Unknown courseConfigType for getSecondValuePropositionSectionForCourseConfigType'
            )
    }
}

export const SplashPageMiddleSection: React.FC<SplashPageMiddleSectionProps> = (
    props
): ReactElement => {
    const color = useMemo((): ColorMap => {
        return getJepColor(props.courseConfigType)
    }, [props.courseConfigType])

    return (
        <div className={styles.splashPageMiddleSection}>
            {props.courseConfigType === CourseConfigType.fsaHealth && (
                <SplashPageSuccessRateSection />
            )}
            <div className={styles.valuePropositionContainer}>
                <ErrorBoundary fallback={<div />}>
                    <ValueProposition
                        title={getAiAssistantTitleForCourseConfigType(
                            props.courseConfigType
                        )}
                        description={getAiAssistantDescriptionForCourseConfigType(
                            props.courseConfigType
                        )}
                        image={getAiAssistantMediaForCourseConfigType(
                            props.courseConfigType
                        )}
                        link={getAiAssistantLinkForCourseConfigType(
                            props.courseConfigType
                        )}
                        titleImageSrc={getAiAssistantTitleImageForCourseConfigType(
                            props.courseConfigType
                        )}
                        titleFontColors={{
                            desktop: color.colorThree,
                            mobile: color.colorThree,
                        }}
                        isMobileMode={props.isMobileMode}
                        backgroundColors={{
                            desktop: color.highlight,
                            mobile: color.highlight,
                        }}
                    />
                </ErrorBoundary>

                <div className={styles.multipleValuePropositions}>
                    <ErrorBoundary fallback={<div />}>
                        <ValueProposition
                            title={'One Stop Shop Accessible Anywhere'}
                            description={getOneStopShopDescriptionForCourseConfigType(
                                props.courseConfigType
                            )}
                            image={<PhilosophyOwl />}
                            titleFontColors={{
                                desktop: color.colorFive,
                                mobile: color.colorFive,
                            }}
                            isMobileMode={props.isMobileMode}
                            backgroundColors={{
                                desktop: color.highlight,
                                mobile: color.lightGray,
                            }}
                        />
                    </ErrorBoundary>
                    <ErrorBoundary fallback={<div />}>
                        <ValueProposition
                            title={'Personalized Study Plan'}
                            description={
                                "We recommend a unique action plan each day based on your progress and exam timeline. Focus on studying, and we'll handle the rest."
                            }
                            image={
                                <Screenshot
                                    src={
                                        '/splashPage/middleSection/studyPlanDashboardScreenshot.webp'
                                    }
                                    alt={
                                        'screenshot of recommended action in dash'
                                    }
                                />
                            }
                            titleFontColors={{
                                desktop: color.colorFive,
                                mobile: color.colorThree,
                            }}
                            isMobileMode={props.isMobileMode}
                            backgroundColors={{
                                desktop: color.highlight,
                                mobile: color.highlight,
                            }}
                        />
                    </ErrorBoundary>
                </div>
                {getSecondValuePropositionSectionForCourseConfigType(
                    props.courseConfigType,
                    props.isMobileMode
                )}
            </div>
        </div>
    )
}
